import * as React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';
import _ from 'lodash';
import { StyledInput } from '../../components/Input/Input';
import { Button } from '../../components/Button';
import { Text } from '../../components/Text';

const OwnerEmailFormComponent = styled.form`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
`;

const StyledErrorMessage = styled(Text)`
  position: absolute;
  left: 0px;
  top: 65px;
  font-size: 11px;
  line-height: 138.5%;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.alert};
`;

const StyledFormLabel = styled(Text)`
  width: 100%;
  font-size: 11px;
  line-height: 138.5%;
  margin-bottom: 8px;
`;

const StyledSubmitButton = styled(Button)`
  margin-top: 24px;
`;

export interface IOwnerEmailFormProps {
  onSubmit: (email: string) => void;
  emailSent?: boolean;
  inProgress?: boolean;
  addingDisabled?: boolean;
  customErrorMsg?: string;
}

const OwnerEmailForm: React.FunctionComponent<IOwnerEmailFormProps> = ({
  onSubmit,
  emailSent,
  inProgress,
  addingDisabled,
  customErrorMsg
}) => {
  const formik = useFormik({
    initialValues: {
      email: '',
      emailRequired: true
    },
    initialTouched: {
      email: false
    },
    validationSchema: Yup.object().shape({
      email: Yup.string()
        .email('Ensure that you typed a valid email address')
        .when('emailRequired', {
          is: true,
          then: Yup.string().required('Email address is required')
        })
    }),
    onSubmit: values => {
      onSubmit(values.email);
    },
    validateOnBlur: true
  });

  const getButtonText = () => {
    if (emailSent) {
      return 'Invitation sent';
    }
    return inProgress ? 'Sending invitation...' : 'Send invitation';
  };

  const getEmailError = (
    email?: string
  ): { error: string; hasError: boolean } => {
    if (!email && !formik.touched.email) {
      return { error: undefined, hasError: false };
    }
    if (email && email.includes('.') && email.includes('@')) {
      return {
        error: formik.errors.email,
        hasError: !_.isNil(formik.errors.email)
      };
    }
    if (email === '' && !_.isNil(formik.errors?.email)) {
      return { error: formik.errors.email, hasError: true };
    }
    return { error: undefined, hasError: false };
  };

  const submitButtonDisabled = (): boolean => {
    return (
      !_.isNil(formik.errors.email) ||
      !formik.values.email ||
      emailSent ||
      addingDisabled
    );
  };

  React.useEffect(() => {
    formik.setFieldValue('email', formik.initialValues.email, false);
  }, [emailSent]);

  return (
    <OwnerEmailFormComponent onSubmit={formik.handleSubmit}>
      <StyledFormLabel>Email from Home Owner</StyledFormLabel>
      <StyledInput
        id="email"
        testId="ownerEmail"
        placeholder={emailSent ? 'Home Owner has beed added' : 'Add email'}
        onChange={e => {
          formik.setTouched({ email: true });
          formik.handleChange(e);
        }}
        value={formik.values.email}
        disabled={emailSent}
        error={getEmailError(formik.values.email).hasError}
      />
      <StyledErrorMessage>
        {getEmailError(formik.values.email).error || customErrorMsg}
      </StyledErrorMessage>
      <StyledSubmitButton
        type="submit"
        text={getButtonText()}
        disabled={submitButtonDisabled()}
        pending={inProgress}
      />
    </OwnerEmailFormComponent>
  );
};

export default OwnerEmailForm;
